
/**
 * Mobile Initializer
 * Utility to handle device-specific initialization tasks
 */

// Check if we're running in a Capacitor environment
export const isCapacitorApp = (): boolean => {
  return typeof (window as any).Capacitor !== 'undefined';
};

// Check if we're running on iOS device (native or browser)
export const isIosDevice = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  return /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
};

// Check if we're running in a browser on iOS
export const isIosBrowser = (): boolean => {
  return isIosDevice() && !isCapacitorApp();
};

// Check if we're running in Chrome on iOS
export const isIosChromeOrSafari = (): boolean => {
  if (!isIosBrowser()) return false;
  
  const userAgent = navigator.userAgent || '';
  return /CriOS|Chrome|Safari/.test(userAgent);
};

// Initialize iOS-specific features
export const initializeIOS = (): void => {
  if (!isIosDevice()) return;
  
  console.log('Initializing iOS-specific features');
  
  // Fix for iOS file permission issues
  try {
    // Set appropriate cache control for iOS WebView
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Cache-Control';
    meta.content = 'no-cache, no-store, must-revalidate';
    document.head.appendChild(meta);
    
    // Set pragma for older browsers
    const pragmaMeta = document.createElement('meta');
    pragmaMeta.httpEquiv = 'Pragma';
    pragmaMeta.content = 'no-cache';
    document.head.appendChild(pragmaMeta);
    
    // Set the Content-Security-Policy to help with script execution
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = "default-src * 'self' data: gap: https://ssl.gstatic.com 'unsafe-eval' 'unsafe-inline'; style-src * 'self' 'unsafe-inline'; script-src * 'self' 'unsafe-inline' 'unsafe-eval'; media-src * blob:";
    document.head.appendChild(cspMeta);
    
    // Set storage quota increase request for iOS browsers
    if (isIosBrowser() && navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().then(isPersisted => {
        console.log(`Persistent storage granted: ${isPersisted}`);
      });
    }
    
    console.log('iOS WebView optimizations applied');
  } catch (e) {
    console.error('Failed to apply iOS optimizations:', e);
  }
  
  // For iOS Chrome, increase timeouts and add safeguards
  if (isIosChromeOrSafari()) {
    console.log('Adding iOS Chrome/Safari specific optimizations');
    // This is a flag that can be checked by other parts of the app
    (window as any).__isIOSBrowser = true;
  }
  
  // Attach to app resume events
  document.addEventListener('resume', () => {
    console.log('App resumed on iOS, checking health');
    // Add a slight delay to ensure app is fully resumed
    setTimeout(() => {
      const rootElement = document.getElementById('root');
      // Check if the app appears to be in a white screen state
      if (!rootElement || !rootElement.children.length) {
        console.log('Detected potential white screen after resume, triggering recovery');
        window.location.reload();
      }
    }, 1000);
  });
};

// Initialize Android-specific features
export const initializeAndroid = (): void => {
  if (!isCapacitorApp()) return;
  
  console.log('Initializing Android-specific features');
  
  // Add Android-specific initializations here
};

// Main initialization function
export const initializeMobileApp = (): void => {
  if (isIosDevice()) {
    console.log('Running on iOS device, applying optimizations');
    initializeIOS();
    return;
  }
  
  if (!isCapacitorApp()) {
    console.log('Not running in a mobile environment');
    return;
  }
  
  console.log('Initializing mobile app environment');
  
  const platform = (window as any).Capacitor.getPlatform();
  if (platform === 'ios') {
    initializeIOS();
  } else if (platform === 'android') {
    initializeAndroid();
  }
  
  // Common mobile initialization
  console.log(`Running on ${platform} platform`);
  
  // Prevent script blocking
  document.addEventListener('deviceready', () => {
    console.log('Device is ready');
  });
};
